import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const fallback =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

export default function PropertyPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const fileInputRef = useRef(null);

  const [property, setProperty] = useState(null);
  const [images, setImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [imageFit, setImageFit] = useState("cover");
  const [imageOpen, setImageOpen] = useState(false);

  const [payments, setPayments] = useState([]);
  const [profitShares, setProfitShares] = useState([]);
  const [editingProfit, setEditingProfit] = useState(false);
  const [savingProfit, setSavingProfit] = useState(false);
  const [profitDraft, setProfitDraft] = useState({});

  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [savingEditPayment, setSavingEditPayment] = useState(false);
  const [paymentEditForm, setPaymentEditForm] = useState({
    titulo: "",
    descricao: "",
    valor: "",
    responsavel_user_id: ""
  });

  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState({
    nome: "",
    bairro: "",
    cidade: "",
    endereco: "",
    latitude: "",
    longitude: ""
  });

  const [editingSalePrice, setEditingSalePrice] = useState(false);
  const [salePriceDraft, setSalePriceDraft] = useState("");
  const [savingSalePrice, setSavingSalePrice] = useState(false);

  const [loading, setLoading] = useState(true);
  const [savingImage, setSavingImage] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [selling, setSelling] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellValue, setSellValue] = useState("");
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [paymentForm, setPaymentForm] = useState({
    valor: "",
    titulo: "",
    descricao: "",
    tipo: "comum"
  });

  const [paymentRefunds, setPaymentRefunds] = useState({});

  const isMobile = screenWidth <= 700;
  const isTablet = screenWidth <= 1000;

  const isSold = Number(property?.vendido || 0) === 1;
  const canEdit = property?.canEdit === true;
  const canManage = canEdit && !isSold;

  const activeImage = images[currentImage];

  const paymentListMaxHeight = isMobile ? 430 : isTablet ? 520 : 620;

  useEffect(() => {
    carregarTudo();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const resize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    const keyDown = (e) => {
      if (!imageOpen) return;

      if (e.key === "Escape") {
        setImageOpen(false);
      }

      if (e.key === "ArrowLeft") {
        imagemAnterior();
      }

      if (e.key === "ArrowRight") {
        proximaImagem();
      }
    };

    window.addEventListener("keydown", keyDown);

    return () => {
      window.removeEventListener("keydown", keyDown);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageOpen, images]);

  const carregarTudo = async () => {
    setLoading(true);

    await carregarImovel();
    await carregarImagens();
    await carregarPagamentos();
    await carregarDivisaoLucro();

    setLoading(false);
  };

  const montarDraftDetalhes = (item) => {
    return {
      nome: item?.nome || "",
      bairro: item?.bairro || "",
      cidade: item?.cidade || "",
      endereco: item?.endereco || "",
      latitude:
        item?.latitude === null ||
        item?.latitude === undefined
          ? ""
          : String(item.latitude),
      longitude:
        item?.longitude === null ||
        item?.longitude === undefined
          ? ""
          : String(item.longitude)
    };
  };

  const carregarImovel = async () => {
    try {
      const res = await axios.get(
        `/properties/${id}`,
        {
          withCredentials: true
        }
      );

      setProperty(res.data);
      setSellValue(String(res.data?.valor_venda || ""));
      setSalePriceDraft(String(res.data?.valor_venda || ""));
      setDetailsDraft(montarDraftDetalhes(res.data));
    } catch (err) {
      console.log(err);
      setProperty(null);
    }
  };

  const carregarImagens = async () => {
    try {
      const res = await axios.get(
        `/properties/${id}/images`,
        {
          withCredentials: true
        }
      );

      setImages(res.data || []);
      setCurrentImage(0);
    } catch (err) {
      console.log(err);
      setImages([]);
    }
  };

  const carregarPagamentos = async () => {
    try {
      const res = await axios.get(
        `/properties/${id}/payments`,
        {
          withCredentials: true
        }
      );

      setPayments(res.data || []);
    } catch (err) {
      console.log(err);
      setPayments([]);
    }
  };

  const carregarDivisaoLucro = async () => {
    try {
      const res = await axios.get(
        `/properties/${id}/profit-shares`,
        {
          withCredentials: true
        }
      );

      const shares = res.data || [];

      setProfitShares(shares);

      const draft = {};
      const refunds = {};

      shares.forEach((item) => {
        draft[String(item.user_id)] = Number(item.percentual || 0);
        refunds[String(item.user_id)] = "";
      });

      setProfitDraft(draft);
      setPaymentRefunds(refunds);
    } catch (err) {
      console.log(err);
      setProfitShares([]);
      setProfitDraft({});
      setPaymentRefunds({});
    }
  };

  const updateDetailsDraft = (e) => {
    setDetailsDraft({
      ...detailsDraft,
      [e.target.name]: e.target.value
    });
  };

  const iniciarEdicaoDetalhes = () => {
    if (!canManage) return;

    setDetailsDraft(montarDraftDetalhes(property));
    setEditingDetails(true);
  };

  const cancelarEdicaoDetalhes = () => {
    setDetailsDraft(montarDraftDetalhes(property));
    setEditingDetails(false);
  };

  const salvarDetalhes = async () => {
    try {
      if (!canManage) return;

      if (!String(detailsDraft.nome || "").trim()) {
        alert("Digite o nome do imóvel");
        return;
      }

      setSavingDetails(true);

      const res = await axios.put(
        `/properties/${id}/details`,
        detailsDraft,
        {
          withCredentials: true
        }
      );

      setProperty(res.data);
      setDetailsDraft(montarDraftDetalhes(res.data));
      setEditingDetails(false);
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao atualizar dados do imóvel";

      alert(msg);
    }

    setSavingDetails(false);
  };

  const abrirGoogleMaps = () => {
    const temCoordenadas =
      property?.latitude !== null &&
      property?.latitude !== undefined &&
      property?.longitude !== null &&
      property?.longitude !== undefined &&
      property?.latitude !== "" &&
      property?.longitude !== "";

    const enderecoTexto = [
      property?.endereco,
      property?.bairro,
      property?.cidade
    ]
      .filter(Boolean)
      .join(", ");

    const query = temCoordenadas
      ? `${property.latitude},${property.longitude}`
      : enderecoTexto;

    if (!query) {
      alert("Este imóvel ainda não possui localização cadastrada");
      return;
    }

    const url =
      "https://www.google.com/maps/search/?api=1&query=" +
      encodeURIComponent(query);

    window.open(url, "_blank");
  };

  const iniciarEdicaoValorVenda = () => {
    if (!canManage) return;

    setSalePriceDraft(String(property?.valor_venda || ""));
    setEditingSalePrice(true);
  };

  const cancelarEdicaoValorVenda = () => {
    setSalePriceDraft(String(property?.valor_venda || ""));
    setEditingSalePrice(false);
  };

  const salvarValorVenda = async () => {
    try {
      if (!canManage) return;

      const valorFinal = Number(salePriceDraft || 0);

      if (!Number.isFinite(valorFinal) || valorFinal < 0) {
        alert("Digite um valor de venda válido");
        return;
      }

      setSavingSalePrice(true);

      const res = await axios.put(
        `/properties/${id}/sale-price`,
        {
          valor_venda: valorFinal
        },
        {
          withCredentials: true
        }
      );

      setProperty((prev) => ({
        ...prev,
        valor_venda: res.data.valor_venda
      }));

      setSellValue(String(res.data.valor_venda || ""));
      setSalePriceDraft(String(res.data.valor_venda || ""));
      setEditingSalePrice(false);
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao atualizar valor de venda";

      alert(msg);
    }

    setSavingSalePrice(false);
  };

  const abrirModalVenda = () => {
    if (!canManage) return;

    setSellValue(String(property?.valor_venda || ""));
    setShowSellModal(true);
  };

  const fecharModalVenda = () => {
    if (selling) return;

    setShowSellModal(false);
  };

  const confirmarVenda = async () => {
    try {
      if (!canManage) return;

      const valorFinal = Number(sellValue || 0);

      if (!Number.isFinite(valorFinal) || valorFinal < 0) {
        alert("Digite um valor de venda válido");
        return;
      }

      setSelling(true);

      await axios.put(
        `/properties/${id}/sold`,
        {
          valor_venda: valorFinal
        },
        {
          withCredentials: true
        }
      );

      alert("Imóvel marcado como vendido");

      setShowSellModal(false);

      navigate("/imoveis-vendidos");
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao marcar imóvel como vendido";

      alert(msg);
    }

    setSelling(false);
  };

  const selecionarImagem = () => {
    if (!canManage) return;

    fileInputRef.current?.click();
  };

  const abrirImagemGrande = () => {
    if (!activeImage) return;

    setImageOpen(true);
  };

  const adicionarImagens = async (e) => {
    try {
      if (!canManage) return;

      const files = Array.from(e.target.files || []);

      if (files.length === 0) return;

      setSavingImage(true);

      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          continue;
        }

        const imagemComprimida = await comprimirImagem(file);

        await axios.post(
          `/properties/${id}/images`,
          {
            imagem: imagemComprimida
          },
          {
            withCredentials: true
          }
        );
      }

      await carregarImagens();

      e.target.value = "";
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao adicionar imagem";

      alert(msg);
    }

    setSavingImage(false);
  };

  const removerImagemAtual = async () => {
    try {
      if (!canManage) return;

      const image = images[currentImage];

      if (!image) return;

      setSavingImage(true);

      await axios.delete(
        `/properties/images/${image.id}`,
        {
          withCredentials: true
        }
      );

      await carregarImagens();

      setCurrentImage(0);
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao remover imagem";

      alert(msg);
    }

    setSavingImage(false);
  };

  const imagemAnterior = () => {
    if (images.length === 0) return;

    setCurrentImage((prev) => {
      if (prev === 0) {
        return images.length - 1;
      }

      return prev - 1;
    });
  };

  const proximaImagem = () => {
    if (images.length === 0) return;

    setCurrentImage((prev) => {
      if (prev === images.length - 1) {
        return 0;
      }

      return prev + 1;
    });
  };

  const alternarAjusteImagem = () => {
    setImageFit((prev) => {
      return prev === "cover" ? "contain" : "cover";
    });
  };

  const comprimirImagem = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();

      const imageUrl = URL.createObjectURL(file);

      img.onload = () => {
        let maxWidth = 1400;
        let maxHeight = 900;
        let quality = 0.7;

        let width = img.width;
        let height = img.height;

        const ratio = Math.min(
          maxWidth / width,
          maxHeight / height,
          1
        );

        width = Math.round(width * ratio);
        height = Math.round(height * ratio);

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const desenhar = (w, h, q) => {
          canvas.width = w;
          canvas.height = h;

          ctx.clearRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);

          return canvas.toDataURL("image/jpeg", q);
        };

        let base64 = desenhar(width, height, quality);

        let tentativas = 0;

        while (base64.length > 850000 && tentativas < 10) {
          width = Math.round(width * 0.86);
          height = Math.round(height * 0.86);
          quality = Math.max(quality - 0.07, 0.35);

          base64 = desenhar(width, height, quality);

          tentativas++;
        }

        URL.revokeObjectURL(imageUrl);

        if (base64.length > 850000) {
          reject(
            new Error(
              "Imagem muito grande. Escolha uma imagem menor."
            )
          );

          return;
        }

        resolve(base64);
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error("Erro ao carregar imagem"));
      };

      img.src = imageUrl;
    });
  };

  const updatePayment = (e) => {
    const { name, value } = e.target;

    setPaymentForm({
      ...paymentForm,
      [name]: value
    });
  };

  const updateRefundValue = (userId, value) => {
    setPaymentRefunds({
      ...paymentRefunds,
      [String(userId)]: value
    });
  };

  const limparFormularioGasto = () => {
    const refunds = {};

    profitShares.forEach((item) => {
      refunds[String(item.user_id)] = "";
    });

    setPaymentForm({
      valor: "",
      titulo: "",
      descricao: "",
      tipo: "comum"
    });

    setPaymentRefunds(refunds);
  };

  const adicionarPagamento = async () => {
    try {
      if (!canManage) return;

      const valorTotal = Number(paymentForm.valor || 0);

      if (!Number.isFinite(valorTotal) || valorTotal <= 0) {
        alert("Digite o valor total do gasto");
        return;
      }

      if (!paymentForm.titulo.trim()) {
        alert("Digite o título do gasto");
        return;
      }

      setSavingPayment(true);

      if (paymentForm.tipo === "restituicao") {
        const pagamentosPorPessoa = profitShares
          .map((item) => {
            return {
              user_id: item.user_id,
              nome: item.nome,
              valor: Number(paymentRefunds[String(item.user_id)] || 0)
            };
          })
          .filter((item) => item.valor > 0);

        const somaPagamentos = pagamentosPorPessoa.reduce(
          (total, item) => {
            return total + Number(item.valor || 0);
          },
          0
        );

        if (pagamentosPorPessoa.length === 0) {
          alert("Informe quanto cada pessoa pagou");
          setSavingPayment(false);
          return;
        }

        if (Math.abs(somaPagamentos - valorTotal) > 0.01) {
          alert(
            "A soma dos valores pagos pelas pessoas precisa ser igual ao valor total do gasto"
          );
          setSavingPayment(false);
          return;
        }

        for (const pagamento of pagamentosPorPessoa) {
          await axios.post(
            `/properties/${id}/payments`,
            {
              titulo: paymentForm.titulo || "",
              descricao: paymentForm.descricao || "",
              valor: pagamento.valor,
              responsavel_user_id: pagamento.user_id
            },
            {
              withCredentials: true
            }
          );
        }
      } else {
        await axios.post(
          `/properties/${id}/payments`,
          {
            titulo: paymentForm.titulo || "",
            descricao: paymentForm.descricao || "",
            valor: valorTotal,
            responsavel_user_id: null
          },
          {
            withCredentials: true
          }
        );
      }

      limparFormularioGasto();

      await carregarPagamentos();
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao adicionar gasto";

      alert(msg);
    }

    setSavingPayment(false);
  };

  const iniciarEdicaoPagamento = (item) => {
    if (!canManage) return;

    setEditingPaymentId(item.id);

    setPaymentEditForm({
      titulo: item.titulo || "",
      descricao: item.descricao || "",
      valor: String(item.valor || ""),
      responsavel_user_id:
        item.responsavel_user_id === null ||
        item.responsavel_user_id === undefined
          ? ""
          : String(item.responsavel_user_id)
    });
  };

  const cancelarEdicaoPagamento = () => {
    setEditingPaymentId(null);

    setPaymentEditForm({
      titulo: "",
      descricao: "",
      valor: "",
      responsavel_user_id: ""
    });
  };

  const updatePaymentEdit = (e) => {
    const { name, value } = e.target;

    setPaymentEditForm({
      ...paymentEditForm,
      [name]: value
    });
  };

  const salvarPagamentoEditado = async (item) => {
    try {
      if (!canManage) return;

      const valorFinal = Number(paymentEditForm.valor || 0);

      if (!Number.isFinite(valorFinal) || valorFinal < 0) {
        alert("Digite um valor válido");
        return;
      }

      const isImovel = item.titulo === "Imóvel";

      if (!isImovel && !paymentEditForm.titulo.trim()) {
        alert("Digite o título do gasto");
        return;
      }

      setSavingEditPayment(true);

      if (item.automatico) {
        await axios.put(
          `/properties/${id}/property-value`,
          {
            valor_pago: valorFinal,
            descricao:
              paymentEditForm.descricao ||
              "Valor pago pelo imóvel"
          },
          {
            withCredentials: true
          }
        );
      } else {
        await axios.put(
          `/properties/payments/${item.id}`,
          {
            titulo: isImovel ? "Imóvel" : paymentEditForm.titulo,
            descricao: paymentEditForm.descricao || "",
            valor: valorFinal,
            responsavel_user_id: isImovel
              ? null
              : paymentEditForm.responsavel_user_id || null
          },
          {
            withCredentials: true
          }
        );
      }

      await carregarPagamentos();
      await carregarImovel();

      cancelarEdicaoPagamento();
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao editar gasto";

      alert(msg);
    }

    setSavingEditPayment(false);
  };

  const removerPagamento = async (paymentId) => {
    try {
      if (!canManage) return;

      await axios.delete(
        `/properties/payments/${paymentId}`,
        {
          withCredentials: true
        }
      );

      await carregarPagamentos();
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao remover gasto";

      alert(msg);
    }
  };

  const updateProfitDraft = (userId, value) => {
    setProfitDraft({
      ...profitDraft,
      [String(userId)]: value
    });
  };

  const totalPercentualLucro = Object.values(profitDraft).reduce(
    (sum, value) => {
      return sum + Number(value || 0);
    },
    0
  );

  const salvarDivisaoLucro = async () => {
    try {
      if (!canManage) return;

      if (Math.abs(totalPercentualLucro - 100) > 0.05) {
        alert("As porcentagens precisam somar 100%");
        return;
      }

      setSavingProfit(true);

      const shares = Object.entries(profitDraft).map(
        ([userId, percentual]) => {
          return {
            user_id: userId,
            percentual: Number(percentual) || 0
          };
        }
      );

      await axios.put(
        `/properties/${id}/profit-shares`,
        {
          shares
        },
        {
          withCredentials: true
        }
      );

      await carregarDivisaoLucro();

      setEditingProfit(false);
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao salvar porcentagens";

      alert(msg);
    }

    setSavingProfit(false);
  };

  const cancelarEdicaoLucro = () => {
    const draft = {};

    profitShares.forEach((item) => {
      draft[String(item.user_id)] = Number(item.percentual || 0);
    });

    setProfitDraft(draft);
    setEditingProfit(false);
  };

  const money = (value) => {
    return Number(value || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    });
  };

  const pagamentosComImovel = (() => {
    const existeImovel = payments.some(
      (item) => item.titulo === "Imóvel"
    );

    if (existeImovel) {
      return payments;
    }

    return [
      {
        id: "imovel-automatico",
        property_id: property?.id,
        titulo: "Imóvel",
        descricao: "Valor pago pelo imóvel",
        valor: Number(property?.valor_pago || 0),
        responsavel_user_id: null,
        automatico: true
      },
      ...payments
    ];
  })();

  const gastosSemRestituicao = pagamentosComImovel.filter((item) => {
    return !item.responsavel_user_id;
  });

  const gastosComRestituicao = pagamentosComImovel.filter((item) => {
    return item.responsavel_user_id;
  });

  const totalGastosSemRestituicao = gastosSemRestituicao.reduce(
    (total, item) => {
      return total + Number(item.valor || 0);
    },
    0
  );

  const totalGastosComRestituicao = gastosComRestituicao.reduce(
    (total, item) => {
      return total + Number(item.valor || 0);
    },
    0
  );

  const totalGastosCadastrados =
    totalGastosSemRestituicao + totalGastosComRestituicao;

  const qtdParticipantes = profitShares.length || 1;

  const gastoSemRestituicaoPorPessoa =
    totalGastosSemRestituicao / qtdParticipantes;

  const gastoComRestituicaoPorPessoa =
    totalGastosComRestituicao / qtdParticipantes;

  const totalPagoComRestituicaoUsuario = (userId) => {
    return gastosComRestituicao
      .filter((item) => {
        return String(item.responsavel_user_id || "") === String(userId);
      })
      .reduce((total, item) => {
        return total + Number(item.valor || 0);
      }, 0);
  };

  const calcularValoresUsuario = (item) => {
    const percentualAtual = editingProfit
      ? Number(profitDraft[String(item.user_id)] || 0)
      : Number(item.percentual || 0);

    const parteVenda =
      Number(property.valor_venda || 0) *
      (percentualAtual / 100);

    const totalPagoRestituicao =
      totalPagoComRestituicaoUsuario(item.user_id);

    const saldoRestituicao =
      totalPagoRestituicao - gastoComRestituicaoPorPessoa;

    const valorFinal =
      parteVenda -
      gastoSemRestituicaoPorPessoa +
      saldoRestituicao;

    return {
      percentualAtual,
      parteVenda,
      totalPagoRestituicao,
      saldoRestituicao,
      valorFinal
    };
  };

  const valorTotalFormulario = Number(paymentForm.valor || 0);

  const somaRestituicoesFormulario = Object.values(paymentRefunds).reduce(
    (total, value) => {
      return total + Number(value || 0);
    },
    0
  );

  const diferencaRestituicao =
    valorTotalFormulario - somaRestituicoesFormulario;

  const totalADistribuir =
    Number(property?.valor_venda || 0) -
    totalGastosSemRestituicao;

  const totalADistribuirVendaModal =
    Number(sellValue || 0) -
    totalGastosSemRestituicao;

  const totalPreviewEdicao =
    Number(salePriceDraft || 0) -
    totalGastosSemRestituicao;

  const imageBoxHeight = isMobile ? 260 : isTablet ? 390 : 540;

  if (loading) {
    return (
      <div style={styles.page}>
        <p style={styles.loading}>
          Carregando imóvel...
        </p>
      </div>
    );
  }

  if (!property) {
    return (
      <div style={styles.page}>
        <p style={styles.error}>
          Imóvel não encontrado ou sem permissão.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {canManage && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={adicionarImagens}
          style={styles.hiddenInput}
        />
      )}

      {showSellModal && (
        <div style={styles.sellModalOverlay}>
          <div style={styles.sellModal}>
            <h2 style={styles.sellTitle}>
              Marcar imóvel como vendido
            </h2>

            <p style={styles.sellText}>
              Confirme ou altere o valor de venda final antes de mover este imóvel para a aba de vendidos.
            </p>

            <label style={styles.sellLabel}>
              Valor de venda final
            </label>

            <input
              type="number"
              value={sellValue}
              onChange={(e) => setSellValue(e.target.value)}
              style={styles.sellInput}
              placeholder="Digite o valor final da venda"
            />

            <div style={styles.sellPreview}>
              <div>
                <span>Soma dos gastos total</span>
                <strong>{money(totalGastosCadastrados)}</strong>
              </div>

              <div>
                <span>Total a distribuir</span>
                <strong
                  style={{
                    color:
                      totalADistribuirVendaModal >= 0
                        ? "#86efac"
                        : "#fca5a5"
                  }}
                >
                  {money(totalADistribuirVendaModal)}
                </strong>
              </div>
            </div>

            <div style={styles.sellActions}>
              <button
                onClick={fecharModalVenda}
                disabled={selling}
                style={styles.sellCancelButton}
              >
                Cancelar
              </button>

              <button
                onClick={confirmarVenda}
                disabled={selling}
                style={styles.sellConfirmButton}
              >
                {selling ? "Salvando..." : "Confirmar venda"}
              </button>
            </div>
          </div>
        </div>
      )}

      {imageOpen && activeImage && (
        <div
          style={styles.modalOverlay}
          onClick={() => setImageOpen(false)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setImageOpen(false);
            }}
            style={styles.modalClose}
          >
            ×
          </button>

          <div
            style={styles.modalImageBox}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activeImage.imagem}
              alt={property.nome}
              style={styles.modalImage}
            />
          </div>
        </div>
      )}

      <div
        style={{
          ...styles.container,
          padding: isMobile ? 14 : 24
        }}
      >
        <div style={styles.header}>
          <p style={styles.tag}>
            Página do imóvel
          </p>

          <div style={styles.titleRow}>
            <h1
              style={{
                ...styles.title,
                fontSize: isMobile ? 24 : 36
              }}
            >
              {property.nome}
            </h1>

            {isSold && (
              <span style={styles.soldBadge}>
                Vendido
              </span>
            )}

            {canManage && !editingDetails && (
              <button
                onClick={iniciarEdicaoDetalhes}
                style={styles.editDetailsButton}
              >
                Editar nome/localização
              </button>
            )}
          </div>

          <p style={styles.subtitle}>
            {property.bairro || "Bairro não informado"} •{" "}
            {property.cidade || "Cidade não informada"}
          </p>
        </div>

        {editingDetails && (
          <div style={styles.detailsEditCard}>
            <div style={styles.detailsEditHeader}>
              <div>
                <h2 style={styles.detailsTitle}>
                  Editar nome e localização
                </h2>

                <p style={styles.detailsSubtitle}>
                  Altere o nome do imóvel e os dados de localização.
                </p>
              </div>
            </div>

            <div
              style={{
                ...styles.detailsGrid,
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr"
              }}
            >
              <div style={styles.detailsFieldFull}>
                <label style={styles.detailsLabel}>
                  Nome do imóvel
                </label>

                <input
                  name="nome"
                  value={detailsDraft.nome}
                  onChange={updateDetailsDraft}
                  style={styles.detailsInput}
                  placeholder="Nome do imóvel"
                />
              </div>

              <div>
                <label style={styles.detailsLabel}>
                  Bairro
                </label>

                <input
                  name="bairro"
                  value={detailsDraft.bairro}
                  onChange={updateDetailsDraft}
                  style={styles.detailsInput}
                  placeholder="Bairro"
                />
              </div>

              <div>
                <label style={styles.detailsLabel}>
                  Cidade
                </label>

                <input
                  name="cidade"
                  value={detailsDraft.cidade}
                  onChange={updateDetailsDraft}
                  style={styles.detailsInput}
                  placeholder="Cidade"
                />
              </div>

              <div style={styles.detailsFieldFull}>
                <label style={styles.detailsLabel}>
                  Endereço
                </label>

                <input
                  name="endereco"
                  value={detailsDraft.endereco}
                  onChange={updateDetailsDraft}
                  style={styles.detailsInput}
                  placeholder="Endereço completo"
                />
              </div>

              <div>
                <label style={styles.detailsLabel}>
                  Latitude
                </label>

                <input
                  name="latitude"
                  value={detailsDraft.latitude}
                  onChange={updateDetailsDraft}
                  style={styles.detailsInput}
                  placeholder="Ex: -21.7946"
                />
              </div>

              <div>
                <label style={styles.detailsLabel}>
                  Longitude
                </label>

                <input
                  name="longitude"
                  value={detailsDraft.longitude}
                  onChange={updateDetailsDraft}
                  style={styles.detailsInput}
                  placeholder="Ex: -48.1756"
                />
              </div>
            </div>

            <div style={styles.detailsActions}>
              <button
                onClick={cancelarEdicaoDetalhes}
                disabled={savingDetails}
                style={styles.detailsCancelButton}
              >
                Cancelar
              </button>

              <button
                onClick={salvarDetalhes}
                disabled={savingDetails}
                style={styles.detailsSaveButton}
              >
                {savingDetails ? "Salvando..." : "Salvar alterações"}
              </button>
            </div>
          </div>
        )}

        <div
          style={{
            ...styles.mainGrid,
            gridTemplateColumns: isTablet ? "1fr" : "1.3fr .7fr"
          }}
        >
          <div style={styles.left}>
            <div style={styles.galleryCard}>
              <div
                style={{
                  ...styles.imageBox,
                  height: imageBoxHeight
                }}
              >
                {activeImage ? (
                  <img
                    src={activeImage.imagem}
                    alt={property.nome}
                    onClick={abrirImagemGrande}
                    style={{
                      ...styles.image,
                      objectFit: imageFit,
                      cursor: "zoom-in"
                    }}
                  />
                ) : (
                  <div style={styles.defaultImage}>
                    <div style={styles.houseIcon}>
                      🏠
                    </div>

                    <div style={styles.defaultText}>
                      Imagem padrão do imóvel
                    </div>
                  </div>
                )}

                {images.length > 1 && (
                  <>
                    <button
                      onClick={imagemAnterior}
                      style={{
                        ...styles.arrowButton,
                        left: 14
                      }}
                    >
                      ‹
                    </button>

                    <button
                      onClick={proximaImagem}
                      style={{
                        ...styles.arrowButton,
                        right: 14
                      }}
                    >
                      ›
                    </button>
                  </>
                )}

                <div style={styles.imageCounter}>
                  {images.length > 0
                    ? `${currentImage + 1} / ${images.length}`
                    : "0 / 0"}
                </div>

                {canManage && (
                  <div style={styles.imageActions}>
                    <button
                      onClick={selecionarImagem}
                      disabled={savingImage}
                      style={styles.imageButton}
                    >
                      {savingImage ? "Salvando..." : "Adicionar imagem"}
                    </button>

                    {activeImage && (
                      <button
                        onClick={alternarAjusteImagem}
                        disabled={savingImage}
                        style={styles.fitButton}
                      >
                        {imageFit === "cover" ? "Imagem inteira" : "Preencher"}
                      </button>
                    )}

                    {activeImage && (
                      <button
                        onClick={removerImagemAtual}
                        disabled={savingImage}
                        style={styles.removeImageButton}
                      >
                        Remover
                      </button>
                    )}
                  </div>
                )}
              </div>

              {images.length > 0 && (
                <div style={styles.thumbnails}>
                  {images.map((img, index) => (
                    <button
                      key={img.id}
                      onClick={() => setCurrentImage(index)}
                      style={{
                        ...styles.thumbnailButton,
                        border:
                          index === currentImage
                            ? "2px solid #60a5fa"
                            : "2px solid transparent"
                      }}
                    >
                      <img
                        src={img.imagem}
                        alt={`Imagem ${index + 1}`}
                        style={styles.thumbnailImage}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.paymentsCard}>
              <div style={styles.paymentsHeader}>
                <div>
                  <h2 style={styles.sectionTitle}>
                    Adicionar gastos
                  </h2>

                  <p style={styles.sectionSubtitle}>
                    Você pode editar o valor do imóvel ou qualquer gasto cadastrado.
                  </p>
                </div>

                <div style={styles.totalBox}>
                  <span>Total cadastrado</span>
                  <strong>{money(totalGastosCadastrados)}</strong>
                </div>
              </div>

              {canManage && (
                <div style={styles.paymentFormBox}>
                  <div style={styles.paymentTotalTop}>
                    <label style={styles.paymentLabel}>
                      Valor total do gasto
                    </label>

                    <input
                      name="valor"
                      type="number"
                      placeholder="Ex: 1000"
                      value={paymentForm.valor}
                      onChange={updatePayment}
                      style={styles.paymentTotalInput}
                    />
                  </div>

                  <div
                    style={{
                      ...styles.paymentForm,
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr"
                    }}
                  >
                    <div>
                      <label style={styles.paymentLabel}>
                        Título do gasto
                      </label>

                      <input
                        name="titulo"
                        placeholder="Ex: Reforma"
                        value={paymentForm.titulo}
                        onChange={updatePayment}
                        style={styles.input}
                      />
                    </div>

                    <div>
                      <label style={styles.paymentLabel}>
                        Tipo do gasto
                      </label>

                      <select
                        name="tipo"
                        value={paymentForm.tipo}
                        onChange={updatePayment}
                        style={styles.input}
                      >
                        <option value="comum">
                          Sem restituição — descontar igualmente
                        </option>

                        <option value="restituicao">
                          Com restituição — informar quem pagou
                        </option>
                      </select>
                    </div>

                    <div style={styles.detailsFieldFull}>
                      <label style={styles.paymentLabel}>
                        Descrição opcional
                      </label>

                      <input
                        name="descricao"
                        placeholder="Descrição opcional"
                        value={paymentForm.descricao}
                        onChange={updatePayment}
                        style={styles.input}
                      />
                    </div>
                  </div>

                  {paymentForm.tipo === "restituicao" && (
                    <div style={styles.refundBox}>
                      <div style={styles.refundHeader}>
                        <div>
                          <h3 style={styles.refundTitle}>
                            Quem pagou esse gasto?
                          </h3>

                          <p style={styles.refundSubtitle}>
                            A soma precisa ser igual ao valor total do gasto.
                          </p>
                        </div>

                        <div
                          style={{
                            ...styles.refundTotalBadge,
                            color:
                              Math.abs(diferencaRestituicao) <= 0.01
                                ? "#86efac"
                                : "#fca5a5"
                          }}
                        >
                          Falta: {money(diferencaRestituicao)}
                        </div>
                      </div>

                      <div style={styles.refundPeopleList}>
                        {profitShares.map((item) => (
                          <div key={item.user_id} style={styles.refundPerson}>
                            <div style={styles.refundPersonInfo}>
                              <img
                                src={item.foto || fallback}
                                alt={item.nome}
                                style={styles.refundAvatar}
                              />

                              <div>
                                <strong>{item.nome}</strong>
                                <span>{item.email}</span>
                              </div>
                            </div>

                            <input
                              type="number"
                              value={paymentRefunds[String(item.user_id)] || ""}
                              onChange={(e) =>
                                updateRefundValue(item.user_id, e.target.value)
                              }
                              style={styles.refundInput}
                              placeholder="Valor pago"
                            />
                          </div>
                        ))}
                      </div>

                      <div style={styles.refundSummary}>
                        <span>
                          Valor total informado:{" "}
                          <strong>{money(valorTotalFormulario)}</strong>
                        </span>

                        <span>
                          Soma paga pelas pessoas:{" "}
                          <strong>{money(somaRestituicoesFormulario)}</strong>
                        </span>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={adicionarPagamento}
                    disabled={savingPayment}
                    style={{
                      ...styles.addButton,
                      opacity: savingPayment ? 0.7 : 1,
                      cursor: savingPayment ? "not-allowed" : "pointer"
                    }}
                  >
                    {savingPayment ? "Adicionando..." : "Adicionar gasto"}
                  </button>
                </div>
              )}

              <div
                style={{
                  ...styles.paymentList,
                  maxHeight: paymentListMaxHeight
                }}
              >
                {pagamentosComImovel.map((item, index) => {
                  const isImovel = item.titulo === "Imóvel";
                  const editingThisPayment =
                    String(editingPaymentId) === String(item.id);

                  return (
                    <div
                      key={item.id}
                      style={{
                        ...styles.paymentItem,
                        border: isImovel
                          ? "1px solid rgba(96,165,250,0.32)"
                          : "1px solid rgba(255,255,255,0.06)",
                        background: isImovel
                          ? "rgba(37,99,235,0.12)"
                          : "#111827"
                      }}
                    >
                      <div style={styles.paymentNumber}>
                        {index + 1}
                      </div>

                      {editingThisPayment ? (
                        <div style={styles.paymentEditBox}>
                          <div
                            style={{
                              ...styles.paymentEditGrid,
                              gridTemplateColumns: isMobile
                                ? "1fr"
                                : "1fr 150px"
                            }}
                          >
                            <div>
                              <label style={styles.paymentLabel}>
                                Título
                              </label>

                              <input
                                name="titulo"
                                value={paymentEditForm.titulo}
                                onChange={updatePaymentEdit}
                                disabled={isImovel}
                                style={{
                                  ...styles.input,
                                  opacity: isImovel ? 0.65 : 1
                                }}
                              />
                            </div>

                            <div>
                              <label style={styles.paymentLabel}>
                                Valor
                              </label>

                              <input
                                name="valor"
                                type="number"
                                value={paymentEditForm.valor}
                                onChange={updatePaymentEdit}
                                style={styles.input}
                              />
                            </div>

                            <div style={styles.detailsFieldFull}>
                              <label style={styles.paymentLabel}>
                                Descrição
                              </label>

                              <input
                                name="descricao"
                                value={paymentEditForm.descricao}
                                onChange={updatePaymentEdit}
                                style={styles.input}
                              />
                            </div>

                            {!isImovel && (
                              <div style={styles.detailsFieldFull}>
                                <label style={styles.paymentLabel}>
                                  Tipo / responsável
                                </label>

                                <select
                                  name="responsavel_user_id"
                                  value={paymentEditForm.responsavel_user_id}
                                  onChange={updatePaymentEdit}
                                  style={styles.input}
                                >
                                  <option value="">
                                    Sem restituição — descontar igualmente
                                  </option>

                                  {profitShares.map((user) => (
                                    <option
                                      key={user.user_id}
                                      value={user.user_id}
                                    >
                                      Pago por {user.nome} — com restituição
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>

                          <div style={styles.paymentEditActions}>
                            <button
                              onClick={cancelarEdicaoPagamento}
                              disabled={savingEditPayment}
                              style={styles.paymentCancelEditButton}
                            >
                              Cancelar
                            </button>

                            <button
                              onClick={() => salvarPagamentoEditado(item)}
                              disabled={savingEditPayment}
                              style={styles.paymentSaveEditButton}
                            >
                              {savingEditPayment
                                ? "Salvando..."
                                : "Salvar gasto"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={styles.paymentContent}>
                            <div style={styles.paymentTop}>
                              <strong style={styles.paymentTitle}>
                                {item.titulo}
                              </strong>

                              <strong style={styles.paymentValue}>
                                {money(item.valor)}
                              </strong>
                            </div>

                            <p style={styles.paymentSplit}>
                              {item.responsavel_user_id
                                ? `Pago por ${
                                    item.responsavel_nome || "Usuário"
                                  } • entra no saldo líquido da restituição`
                                : "Gasto sem restituição • descontado igualmente"}
                            </p>

                            {item.descricao && (
                              <p style={styles.paymentDescription}>
                                {item.descricao}
                              </p>
                            )}
                          </div>

                          {canManage && (
                            <div style={styles.paymentActions}>
                              <button
                                onClick={() => iniciarEdicaoPagamento(item)}
                                style={styles.editPaymentButton}
                              >
                                Editar
                              </button>

                              {!isImovel && !item.automatico && (
                                <button
                                  onClick={() => removerPagamento(item.id)}
                                  style={styles.removeButton}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={styles.right}>
            <div style={styles.priceCard}>
              <div style={styles.priceHeader}>
                <p style={styles.priceLabel}>
                  Valor por venda
                </p>

                {canManage && !editingSalePrice && (
                  <button
                    onClick={iniciarEdicaoValorVenda}
                    style={styles.editSaleButton}
                  >
                    Editar valor
                  </button>
                )}
              </div>

              {!editingSalePrice ? (
                <h2
                  style={{
                    ...styles.salePrice,
                    fontSize: isMobile ? 28 : 38
                  }}
                >
                  {money(property.valor_venda)}
                </h2>
              ) : (
                <div style={styles.saleEditBox}>
                  <input
                    type="number"
                    value={salePriceDraft}
                    onChange={(e) => setSalePriceDraft(e.target.value)}
                    style={styles.saleEditInput}
                    placeholder="Valor por venda"
                  />

                  <div style={styles.saleEditPreview}>
                    Novo total a distribuir:{" "}
                    <strong
                      style={{
                        color:
                          totalPreviewEdicao >= 0
                            ? "#86efac"
                            : "#fca5a5"
                      }}
                    >
                      {money(totalPreviewEdicao)}
                    </strong>
                  </div>

                  <div style={styles.saleEditActions}>
                    <button
                      onClick={cancelarEdicaoValorVenda}
                      disabled={savingSalePrice}
                      style={styles.saleCancelButton}
                    >
                      Cancelar
                    </button>

                    <button
                      onClick={salvarValorVenda}
                      disabled={savingSalePrice}
                      style={styles.saleSaveButton}
                    >
                      {savingSalePrice ? "Salvando..." : "Salvar valor"}
                    </button>
                  </div>
                </div>
              )}

              <div style={styles.divider} />

              <div style={styles.priceRow}>
                <span>Soma dos gastos total</span>
                <strong>{money(totalGastosCadastrados)}</strong>
              </div>

              <div style={styles.priceRow}>
                <span>Total a distribuir</span>

                <strong
                  style={{
                    color: totalADistribuir >= 0 ? "#22c55e" : "#ef4444"
                  }}
                >
                  {money(totalADistribuir)}
                </strong>
              </div>

              {canManage && (
                <button
                  onClick={abrirModalVenda}
                  disabled={
                    selling ||
                    editingSalePrice ||
                    editingDetails ||
                    editingPaymentId !== null
                  }
                  style={{
                    ...styles.soldButton,
                    opacity:
                      selling ||
                      editingSalePrice ||
                      editingDetails ||
                      editingPaymentId !== null
                        ? 0.7
                        : 1,
                    cursor:
                      selling ||
                      editingSalePrice ||
                      editingDetails ||
                      editingPaymentId !== null
                        ? "not-allowed"
                        : "pointer"
                  }}
                >
                  {selling ? "Marcando..." : "Marcar como vendido"}
                </button>
              )}

              {isSold && (
                <div style={styles.soldInfo}>
                  Este imóvel já foi vendido e está disponível em Imóveis Vendidos.
                </div>
              )}
            </div>

            <div style={styles.sideCard}>
              <div style={styles.locationTitleRow}>
                <h3 style={styles.sideTitle}>
                  Localização
                </h3>

                {canManage && (
                  <button
                    onClick={iniciarEdicaoDetalhes}
                    style={styles.locationEditButton}
                  >
                    Editar
                  </button>
                )}
              </div>

              <div style={styles.locationBox}>
                <button
                  onClick={abrirGoogleMaps}
                  style={styles.locationIconButton}
                  title="Abrir localização no Google Maps"
                >
                  📍
                </button>

                <div>
                  <strong style={styles.locationMain}>
                    {property.bairro || "Bairro não informado"}
                  </strong>

                  <p style={styles.locationText}>
                    {property.cidade || "Cidade não informada"}
                  </p>

                  <p style={styles.locationText}>
                    {property.endereco || "Endereço não informado"}
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.sideCard}>
              <div style={styles.profitHeader}>
                <h3 style={styles.sideTitle}>
                  Valor final por usuário
                </h3>

                {canManage && !editingProfit && (
                  <button
                    onClick={() => setEditingProfit(true)}
                    style={styles.smallEditButton}
                  >
                    Editar %
                  </button>
                )}
              </div>

              <p style={styles.profitSubtitle}>
                Final = parte da venda - gastos sem restituição + saldo líquido da restituição.
              </p>

              {editingProfit && (
                <div
                  style={{
                    ...styles.percentTotalBox,
                    color:
                      Math.abs(totalPercentualLucro - 100) <= 0.05
                        ? "#86efac"
                        : "#fca5a5"
                  }}
                >
                  Total: {totalPercentualLucro.toFixed(2)}%
                </div>
              )}

              <div style={styles.profitShareList}>
                {profitShares.map((item) => {
                  const dados = calcularValoresUsuario(item);

                  return (
                    <div key={item.user_id} style={styles.profitShareItem}>
                      <div style={styles.profitShareUser}>
                        <img
                          src={item.foto || fallback}
                          alt={item.nome}
                          style={styles.avatarSmall}
                        />

                        <div style={styles.profitShareUserText}>
                          <strong>{item.nome}</strong>

                          <span>
                            Parte da venda: {money(dados.parteVenda)}
                          </span>

                          {gastoSemRestituicaoPorPessoa > 0 && (
                            <span>
                              Gasto sem restituição: -
                              {money(gastoSemRestituicaoPorPessoa)}
                            </span>
                          )}

                          {totalGastosComRestituicao > 0 && (
                            <>
                              <span style={styles.formulaText}>
                                Parte dele nos gastos com restituição:{" "}
                                {money(gastoComRestituicaoPorPessoa)}
                              </span>

                              {dados.totalPagoRestituicao > 0 && (
                                <span style={styles.formulaText}>
                                  Valor que ele pagou:{" "}
                                  {money(dados.totalPagoRestituicao)}
                                </span>
                              )}

                              <span
                                style={{
                                  color:
                                    dados.saldoRestituicao >= 0
                                      ? "#86efac"
                                      : "#fca5a5"
                                }}
                              >
                                {dados.saldoRestituicao >= 0
                                  ? "Saldo líquido da restituição: +"
                                  : "Saldo líquido da restituição: -"}
                                {money(Math.abs(dados.saldoRestituicao))}
                              </span>
                            </>
                          )}

                          <b
                            style={{
                              color:
                                dados.valorFinal >= 0
                                  ? "#86efac"
                                  : "#fca5a5"
                            }}
                          >
                            Final a receber: {money(dados.valorFinal)}
                          </b>
                        </div>
                      </div>

                      {editingProfit ? (
                        <input
                          type="number"
                          value={profitDraft[String(item.user_id)] ?? ""}
                          onChange={(e) =>
                            updateProfitDraft(item.user_id, e.target.value)
                          }
                          style={styles.percentInput}
                        />
                      ) : (
                        <span style={styles.percentBadge}>
                          {Number(item.percentual || 0).toFixed(2)}%
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {editingProfit && (
                <div style={styles.profitActions}>
                  <button
                    onClick={cancelarEdicaoLucro}
                    style={styles.cancelButton}
                  >
                    Cancelar
                  </button>

                  <button
                    onClick={salvarDivisaoLucro}
                    disabled={savingProfit}
                    style={styles.saveButton}
                  >
                    {savingProfit ? "Salvando..." : "Salvar"}
                  </button>
                </div>
              )}

              {!canEdit && (
                <div style={styles.viewerBadge}>
                  Modo visualização
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    background: "#0f172a",
    color: "#fff",
    boxSizing: "border-box"
  },

  hiddenInput: {
    display: "none"
  },

  container: {
    width: "100%",
    maxWidth: 1440,
    margin: "0 auto",
    boxSizing: "border-box"
  },

  loading: {
    color: "#cbd5e1",
    padding: 30
  },

  error: {
    color: "#ef4444",
    padding: 30
  },

  header: {
    marginBottom: 22
  },

  tag: {
    color: "#60a5fa",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 1.4,
    marginBottom: 8
  },

  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap"
  },

  title: {
    margin: 0,
    color: "#f8fafc",
    fontWeight: 800,
    lineHeight: 1.1
  },

  soldBadge: {
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(34,197,94,0.15)",
    color: "#86efac",
    border: "1px solid rgba(34,197,94,0.35)",
    fontSize: 13
  },

  editDetailsButton: {
    padding: "9px 12px",
    borderRadius: 12,
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.16)",
    color: "#bfdbfe",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: 12
  },

  subtitle: {
    marginTop: 10,
    color: "#94a3b8",
    fontSize: 16
  },

  detailsEditCard: {
    background: "rgba(15,23,42,0.96)",
    border: "1px solid rgba(96,165,250,0.22)",
    borderRadius: 22,
    padding: 22,
    marginBottom: 22,
    boxSizing: "border-box"
  },

  detailsEditHeader: {
    marginBottom: 16
  },

  detailsTitle: {
    margin: 0,
    color: "#f8fafc",
    fontSize: 21
  },

  detailsSubtitle: {
    margin: "7px 0 0",
    color: "#94a3b8",
    fontSize: 13
  },

  detailsGrid: {
    display: "grid",
    gap: 12
  },

  detailsFieldFull: {
    gridColumn: "1 / -1"
  },

  detailsLabel: {
    display: "block",
    marginBottom: 7,
    color: "#cbd5e1",
    fontSize: 13
  },

  detailsInput: {
    width: "100%",
    padding: 13,
    borderRadius: 12,
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box"
  },

  detailsActions: {
    display: "flex",
    gap: 10,
    marginTop: 16,
    justifyContent: "flex-end",
    flexWrap: "wrap"
  },

  detailsCancelButton: {
    padding: "12px 16px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    cursor: "pointer"
  },

  detailsSaveButton: {
    padding: "12px 16px",
    borderRadius: 12,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "bold"
  },

  mainGrid: {
    display: "grid",
    gap: 22,
    alignItems: "start"
  },

  left: {
    display: "flex",
    flexDirection: "column",
    gap: 18
  },

  right: {
    display: "flex",
    flexDirection: "column",
    gap: 18
  },

  galleryCard: {
    width: "100%"
  },

  imageBox: {
    width: "100%",
    borderRadius: 24,
    overflow: "hidden",
    background: "#020617",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 24px 70px rgba(0,0,0,0.35)",
    position: "relative"
  },

  image: {
    width: "100%",
    height: "100%",
    display: "block",
    background: "#020617"
  },

  defaultImage: {
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(135deg, #111827 0%, #1e293b 45%, #0f172a 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b"
  },

  houseIcon: {
    fontSize: 64,
    marginBottom: 12
  },

  defaultText: {
    fontSize: 15
  },

  arrowButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(15,23,42,0.8)",
    color: "#fff",
    fontSize: 34,
    lineHeight: "34px",
    cursor: "pointer",
    backdropFilter: "blur(10px)"
  },

  imageCounter: {
    position: "absolute",
    top: 16,
    right: 16,
    background: "rgba(15,23,42,0.82)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#fff",
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 12,
    backdropFilter: "blur(10px)"
  },

  imageActions: {
    position: "absolute",
    right: 16,
    bottom: 16,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end"
  },

  imageButton: {
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(15,23,42,0.86)",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 12,
    cursor: "pointer",
    backdropFilter: "blur(10px)"
  },

  fitButton: {
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.2)",
    color: "#bfdbfe",
    padding: "10px 14px",
    borderRadius: 12,
    cursor: "pointer",
    backdropFilter: "blur(10px)"
  },

  removeImageButton: {
    border: "1px solid rgba(239,68,68,0.35)",
    background: "rgba(239,68,68,0.16)",
    color: "#fecaca",
    padding: "10px 14px",
    borderRadius: 12,
    cursor: "pointer",
    backdropFilter: "blur(10px)"
  },

  thumbnails: {
    marginTop: 12,
    display: "flex",
    gap: 10,
    overflowX: "auto",
    paddingBottom: 4
  },

  thumbnailButton: {
    width: 86,
    height: 62,
    borderRadius: 12,
    overflow: "hidden",
    padding: 0,
    background: "#111827",
    cursor: "pointer",
    flexShrink: 0
  },

  thumbnailImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block"
  },

  paymentsCard: {
    background: "rgba(15,23,42,0.95)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 20,
    padding: 22,
    boxSizing: "border-box"
  },

  paymentsHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: 18
  },

  sectionTitle: {
    margin: "0 0 6px 0",
    fontSize: 20,
    color: "#f8fafc"
  },

  sectionSubtitle: {
    margin: 0,
    color: "#94a3b8",
    fontSize: 13,
    lineHeight: 1.5
  },

  totalBox: {
    minWidth: 130,
    background: "rgba(37,99,235,0.14)",
    border: "1px solid rgba(96,165,250,0.25)",
    borderRadius: 14,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    color: "#93c5fd"
  },

  paymentFormBox: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 18,
    background: "#020617",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 18,
    padding: 16
  },

  paymentTotalTop: {
    background: "rgba(37,99,235,0.10)",
    border: "1px solid rgba(96,165,250,0.20)",
    borderRadius: 16,
    padding: 14
  },

  paymentLabel: {
    display: "block",
    color: "#cbd5e1",
    fontSize: 13,
    marginBottom: 7
  },

  paymentTotalInput: {
    width: "100%",
    padding: 15,
    borderRadius: 14,
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box",
    fontSize: 18,
    fontWeight: "bold"
  },

  paymentForm: {
    display: "grid",
    gap: 10
  },

  input: {
    width: "100%",
    padding: 13,
    borderRadius: 12,
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box"
  },

  refundBox: {
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 14
  },

  refundHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12
  },

  refundTitle: {
    margin: 0,
    color: "#f8fafc",
    fontSize: 16
  },

  refundSubtitle: {
    margin: "5px 0 0",
    color: "#94a3b8",
    fontSize: 12
  },

  refundTotalBadge: {
    padding: "8px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.08)",
    fontSize: 12,
    height: "fit-content"
  },

  refundPeopleList: {
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  refundPerson: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    background: "#020617",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 14,
    padding: 12,
    flexWrap: "wrap"
  },

  refundPersonInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0
  },

  refundAvatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    objectFit: "cover"
  },

  refundInput: {
    width: 130,
    padding: 11,
    borderRadius: 12,
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box"
  },

  refundSummary: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px solid rgba(255,255,255,0.08)",
    color: "#cbd5e1",
    fontSize: 13
  },

  addButton: {
    padding: 13,
    border: "none",
    borderRadius: 12,
    background: "#2563eb",
    color: "#fff",
    fontWeight: "bold"
  },

  paymentList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 8,
    paddingBottom: 2,
    scrollbarWidth: "thin",
    overscrollBehavior: "contain"
  },

  paymentItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    borderRadius: 16,
    padding: 14,
    boxSizing: "border-box"
  },

  paymentNumber: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "rgba(37,99,235,0.16)",
    color: "#93c5fd",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: "bold",
    flexShrink: 0
  },

  paymentContent: {
    flex: 1,
    minWidth: 0
  },

  paymentTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap"
  },

  paymentTitle: {
    color: "#f8fafc",
    fontSize: 15
  },

  paymentValue: {
    color: "#22c55e",
    fontSize: 15
  },

  paymentSplit: {
    margin: "6px 0 0",
    color: "#93c5fd",
    fontSize: 12
  },

  paymentDescription: {
    margin: "6px 0 0",
    color: "#94a3b8",
    fontSize: 13,
    lineHeight: 1.4
  },

  paymentActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0
  },

  editPaymentButton: {
    padding: "7px 10px",
    borderRadius: 10,
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.16)",
    color: "#bfdbfe",
    cursor: "pointer",
    fontSize: 12
  },

  removeButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: "1px solid rgba(239,68,68,0.25)",
    background: "rgba(239,68,68,0.10)",
    color: "#ef4444",
    cursor: "pointer",
    flexShrink: 0
  },

  paymentEditBox: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 12
  },

  paymentEditGrid: {
    display: "grid",
    gap: 10
  },

  paymentEditActions: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    flexWrap: "wrap"
  },

  paymentCancelEditButton: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    cursor: "pointer"
  },

  paymentSaveEditButton: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "bold"
  },

  priceCard: {
    background: "linear-gradient(180deg, #111827, #020617)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 22,
    padding: 24,
    boxShadow: "0 24px 70px rgba(0,0,0,0.35)",
    boxSizing: "border-box"
  },

  priceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 8
  },

  priceLabel: {
    margin: 0,
    color: "#94a3b8",
    fontSize: 14
  },

  editSaleButton: {
    padding: "7px 10px",
    borderRadius: 10,
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.16)",
    color: "#bfdbfe",
    cursor: "pointer",
    fontSize: 12
  },

  salePrice: {
    margin: "8px 0 0",
    color: "#f8fafc",
    lineHeight: 1
  },

  saleEditBox: {
    marginTop: 10,
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  saleEditInput: {
    width: "100%",
    padding: 13,
    borderRadius: 12,
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box",
    fontSize: 16
  },

  saleEditPreview: {
    padding: 10,
    borderRadius: 12,
    background: "rgba(255,255,255,0.05)",
    color: "#cbd5e1",
    fontSize: 13
  },

  saleEditActions: {
    display: "flex",
    gap: 10
  },

  saleCancelButton: {
    flex: 1,
    padding: 11,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    cursor: "pointer"
  },

  saleSaveButton: {
    flex: 1,
    padding: 11,
    borderRadius: 12,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "bold"
  },

  divider: {
    height: 1,
    background: "rgba(255,255,255,0.08)",
    margin: "22px 0"
  },

  priceRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    padding: "12px 0",
    color: "#cbd5e1",
    fontSize: 15
  },

  soldButton: {
    width: "100%",
    marginTop: 18,
    padding: 13,
    borderRadius: 14,
    border: "none",
    background: "#22c55e",
    color: "#052e16",
    fontWeight: "bold"
  },

  soldInfo: {
    marginTop: 14,
    padding: 12,
    borderRadius: 14,
    background: "rgba(34,197,94,0.12)",
    color: "#86efac",
    fontSize: 13,
    lineHeight: 1.4
  },

  sideCard: {
    background: "rgba(15,23,42,0.95)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 22,
    padding: 22,
    boxSizing: "border-box"
  },

  sideTitle: {
    margin: 0,
    fontSize: 18
  },

  locationTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12
  },

  locationEditButton: {
    padding: "7px 10px",
    borderRadius: 10,
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.16)",
    color: "#bfdbfe",
    cursor: "pointer",
    fontSize: 12
  },

  locationBox: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    marginTop: 14
  },

  locationIconButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "rgba(37,99,235,0.15)",
    color: "#60a5fa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    border: "1px solid rgba(96,165,250,0.25)",
    cursor: "pointer",
    fontSize: 18
  },

  locationMain: {
    display: "block",
    color: "#f8fafc",
    fontSize: 15,
    marginBottom: 6
  },

  locationText: {
    margin: "0 0 4px",
    color: "#94a3b8",
    fontSize: 14,
    lineHeight: 1.4
  },

  viewerBadge: {
    marginTop: 14,
    padding: "10px 12px",
    borderRadius: 12,
    background: "rgba(148,163,184,0.12)",
    color: "#cbd5e1",
    fontSize: 13,
    textAlign: "center"
  },

  profitHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10
  },

  profitSubtitle: {
    margin: "0 0 14px",
    color: "#94a3b8",
    fontSize: 13,
    lineHeight: 1.4
  },

  smallEditButton: {
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.16)",
    color: "#bfdbfe",
    padding: "7px 10px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 12
  },

  profitShareList: {
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  profitShareItem: {
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 14,
    padding: 12,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12
  },

  profitShareUser: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    minWidth: 0
  },

  profitShareUserText: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
    color: "#cbd5e1",
    fontSize: 12
  },

  formulaText: {
    color: "#64748b",
    fontSize: 11,
    marginTop: 3
  },

  avatarSmall: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0
  },

  percentBadge: {
    background: "rgba(34,197,94,0.12)",
    color: "#86efac",
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 12,
    whiteSpace: "nowrap"
  },

  percentInput: {
    width: 76,
    padding: 9,
    borderRadius: 10,
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    outline: "none",
    textAlign: "center"
  },

  percentTotalBox: {
    padding: "9px 12px",
    borderRadius: 12,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    marginBottom: 12,
    fontSize: 13
  },

  profitActions: {
    display: "flex",
    gap: 10,
    marginTop: 14
  },

  cancelButton: {
    flex: 1,
    padding: 11,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    cursor: "pointer"
  },

  saveButton: {
    flex: 1,
    padding: 11,
    borderRadius: 12,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "bold"
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.88)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    boxSizing: "border-box"
  },

  modalImageBox: {
    maxWidth: "92vw",
    maxHeight: "88vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

  modalImage: {
    maxWidth: "92vw",
    maxHeight: "88vh",
    objectFit: "contain",
    borderRadius: 16,
    boxShadow: "0 30px 100px rgba(0,0,0,0.7)"
  },

  modalClose: {
    position: "fixed",
    top: 20,
    right: 24,
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(15,23,42,0.85)",
    color: "#fff",
    fontSize: 30,
    cursor: "pointer",
    zIndex: 10000
  },

  sellModalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.72)",
    zIndex: 9998,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    boxSizing: "border-box"
  },

  sellModal: {
    width: "100%",
    maxWidth: 460,
    background: "#020617",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 22,
    padding: 24,
    boxShadow: "0 30px 100px rgba(0,0,0,0.65)"
  },

  sellTitle: {
    margin: 0,
    color: "#f8fafc",
    fontSize: 22
  },

  sellText: {
    margin: "10px 0 18px",
    color: "#94a3b8",
    fontSize: 14,
    lineHeight: 1.5
  },

  sellLabel: {
    display: "block",
    marginBottom: 8,
    color: "#cbd5e1",
    fontSize: 13
  },

  sellInput: {
    width: "100%",
    padding: 14,
    borderRadius: 12,
    border: "1px solid #334155",
    background: "#0f172a",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box",
    fontSize: 16
  },

  sellPreview: {
    marginTop: 14,
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  sellActions: {
    display: "flex",
    gap: 10,
    marginTop: 18
  },

  sellCancelButton: {
    flex: 1,
    padding: 12,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    cursor: "pointer"
  },

  sellConfirmButton: {
    flex: 1,
    padding: 12,
    borderRadius: 12,
    border: "none",
    background: "#22c55e",
    color: "#052e16",
    fontWeight: "bold",
    cursor: "pointer"
  }
};